import './sentry'
import './util/handleError'
import './util/setupServiceWorker'

import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import React from './lib/teact/teact'

import { getActions, getGlobal } from './global'
import './global/init'
import updateWebmanifest from './util/updateWebmanifest'
import { IS_MULTITAB_SUPPORTED } from './util/windowEnvironment'

import { APP_VERSION, DEBUG, IS_BETA, MULTITAB_LOCALSTORAGE_KEY } from './config'
import { selectTabState } from './global/selectors'
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole'
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/multitab'
import { onBeforeUnload } from './util/schedulers'

import App from './components/App'

import './styles/reboot.css'
import './styles/index.scss'
// eslint-disable-next-line
;(() => {
  if (IS_BETA) {
    // eslint-disable-next-line no-restricted-globals
    if (typeof self.location !== 'undefined') {
      // eslint-disable-next-line no-restricted-globals
      const url = new URL(self.location.href)
      const dcHost = url.searchParams.get('dc_host')

      if (dcHost) {
        const dcPort = url.searchParams.get('dc_port')

        window.DC_HOST = dcHost
        window.DC_PORT = dcPort ? parseInt(dcPort, 10) : 443

        return
      }
    }
  }

  const dcHostMeta = document.querySelector<HTMLMetaElement>('meta[name="dc-host"]')
  const dcPortMeta = document.querySelector<HTMLMetaElement>('meta[name="dc-port"]')
  const DC_HOST = dcHostMeta?.content
  const DC_PORT = dcPortMeta?.content ? parseInt(dcPortMeta.content, 10) : undefined

  if (!DC_HOST || !DC_PORT) {
    throw new Error('Initialize dc-host and dc-port meta tags in index.html')
  }

  window.DC_HOST = DC_HOST
  window.DC_PORT = DC_PORT
})()

init()

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT')
  }

  if (IS_MULTITAB_SUPPORTED) {
    subscribeToMultitabBroadcastChannel()

    await requestGlobal(APP_VERSION)
    localStorage.setItem(MULTITAB_LOCALSTORAGE_KEY, '1')
    onBeforeUnload(() => {
      const global = getGlobal()
      if (Object.keys(global.byTabId).length === 1) {
        localStorage.removeItem(MULTITAB_LOCALSTORAGE_KEY)
      }
    })
  }

  getActions().initShared()
  getActions().init()

  if (IS_MULTITAB_SUPPORTED) {
    establishMultitabRole()
    subscribeToMasterChange((isMasterTab) => {
      getActions().switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true })
    })
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER')
  }

  updateWebmanifest()

  const container = document.getElementById('root')!
  const root = createRoot(container, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      // eslint-disable-next-line no-console
      console.error('Uncaught error', error, errorInfo.componentStack)
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  })

  root.render(<App />)

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER')
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(getGlobal()))
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', getGlobal())
    })
  }
}
